import React from 'react'
import { NavLink as RouterLink } from 'react-router-dom'
import { addDays, format } from 'date-fns'

import * as Navigation from 'pages/Programming/components/Navigation'
import { Tab, Tabs } from 'components/Tabs'
import { trackEvent } from 'services/segment'
import { useAffiliate } from 'hooks/useAffiliate'

function formatDate(date: Date) {
  return format(date, 'MMM dd')
}

type Props = {
  track: string
  date: Date
  slugs: {
    previousWeek: string
    nextWeek: string
    atHomeTrack: string
    affiliateTrack: string
    competeTrack: string
    liftingTrack: string
  }
}

const WEEKLY_OVERVIEW_ANALYTICS_COMPONENT = 'Weekly Overview Navigation Tab'
const WEEKLY_OVERVIEW_ANALYTICS_EVENT =
  WEEKLY_OVERVIEW_ANALYTICS_COMPONENT + ' Clicked'
const WEEKLY_OVERVIEW_ANALYTICS_LOCATION = 'Affiliate Toolkit'

function WeeklyOverviewNavigation({ track, date, slugs }: Props) {
  const { selectedAffiliate } = useAffiliate()
  const url = window.location.href
  const title = document.title
  const path = window.location.pathname
  const atHomeLiftingCutoffDate = new Date('Mon, 03 Jul 2023 00:00:00 GMT')
  const atHomeLiftingAvailable = date < atHomeLiftingCutoffDate
  const nextDay = new Date(date)
  nextDay.setDate(date.getDate() + 7)
  const forwardNavigationDisabled =
    nextDay >= atHomeLiftingCutoffDate &&
    (slugs?.nextWeek?.includes('lifting') ||
      slugs?.nextWeek?.includes('at-home'))

  return (
    <Navigation.Wrapper>
      <Navigation.Title
        previousTo={slugs.previousWeek}
        nextTo={forwardNavigationDisabled ? null : slugs?.nextWeek}
      >
        {formatDate(date)} - {formatDate(addDays(date, 6))}
      </Navigation.Title>
      <Tabs>
        <Tab
          as={RouterLink}
          to={slugs.affiliateTrack}
          isActive={track === 'affiliate'}
          onClick={() => {
            trackEvent({
              component: WEEKLY_OVERVIEW_ANALYTICS_COMPONENT,
              event: WEEKLY_OVERVIEW_ANALYTICS_EVENT,
              location: WEEKLY_OVERVIEW_ANALYTICS_LOCATION,
              text: 'Affiliate',
              linkUrl: slugs.affiliateTrack,
              affiliateId: selectedAffiliate?.affiliateId,
              userRole:
                selectedAffiliate?.roles?.length > 0
                  ? selectedAffiliate?.roles[0]
                  : null,
              groupId: selectedAffiliate?.groupId,
              context: {
                page: {
                  url,
                  title,
                  path,
                },
              },
            })
          }}
        >
          Affiliate
        </Tab>
        {atHomeLiftingAvailable && (
          <Tab
            as={RouterLink}
            to={slugs.atHomeTrack}
            onClick={() => {
              trackEvent({
                component: WEEKLY_OVERVIEW_ANALYTICS_COMPONENT,
                event: WEEKLY_OVERVIEW_ANALYTICS_EVENT,
                location: WEEKLY_OVERVIEW_ANALYTICS_LOCATION,
                text: 'At Home',
                linkUrl: slugs.atHomeTrack,
                affiliateId: selectedAffiliate?.affiliateId,
                userRole:
                  selectedAffiliate?.roles?.length > 0
                    ? selectedAffiliate?.roles[0]
                    : null,
                groupId: selectedAffiliate?.groupId,
                context: {
                  page: {
                    url,
                    title,
                    path,
                  },
                },
              })
            }}
          >
            At Home
          </Tab>
        )}

        <Tab
          as={RouterLink}
          to={slugs.competeTrack}
          onClick={() => {
            trackEvent({
              component: WEEKLY_OVERVIEW_ANALYTICS_COMPONENT,
              event: WEEKLY_OVERVIEW_ANALYTICS_EVENT,
              location: WEEKLY_OVERVIEW_ANALYTICS_LOCATION,
              text: 'Compete',
              linkUrl: slugs.competeTrack,
              affiliateId: selectedAffiliate?.affiliateId,
              userRole:
                selectedAffiliate?.roles?.length > 0
                  ? selectedAffiliate?.roles[0]
                  : null,
              groupId: selectedAffiliate?.groupId,
              context: {
                page: {
                  url,
                  title,
                  path,
                },
              },
            })
          }}
        >
          Compete
        </Tab>
        {atHomeLiftingAvailable && (
          <Tab
            as={RouterLink}
            to={slugs.liftingTrack}
            onClick={() => {
              trackEvent({
                component: WEEKLY_OVERVIEW_ANALYTICS_COMPONENT,
                event: WEEKLY_OVERVIEW_ANALYTICS_EVENT,
                location: WEEKLY_OVERVIEW_ANALYTICS_LOCATION,
                text: 'Lifting',
                linkUrl: slugs.liftingTrack,
                affiliateId: selectedAffiliate?.affiliateId,
                userRole:
                  selectedAffiliate?.roles?.length > 0
                    ? selectedAffiliate?.roles[0]
                    : null,
                groupId: selectedAffiliate?.groupId,
                context: {
                  page: {
                    url,
                    title,
                    path,
                  },
                },
              })
            }}
          >
            Lifting
          </Tab>
        )}
      </Tabs>
    </Navigation.Wrapper>
  )
}

export default WeeklyOverviewNavigation
